/* jshint esversion: 6 */
/**
 * VersioningAndLockingService is a service class that handles versioning and locking functionalities for a CMS.
 * It interacts with the API, manages modal dialogs, and updates data based on various user actions.
 */
class VersioningAndLockingService {
    /**
     * Constructor for VersioningAndLockingService.
     * @param {Object} $uibModal - Angular UI Bootstrap modal service.
     * @param {Object} $signals - Signal service for emitting events.
     * @param {Object} APIService - Service for making API calls.
     * @param {Object} $routeParams - Service to access route parameters.
     * @param {Object} $rootScope - Root scope of the application.
     * @param {Object} $q - Service for promises.
     */
    constructor($uibModal, $signals, APIService, $routeParams, $rootScope, $q) {
        if (!isCMS) {
            return false;
        }
        this.$uibModal = $uibModal;
        this.$signals = $signals;
        this.APIService = APIService;
        this.$routeParams = $routeParams;
        this.$rootScope = $rootScope;
        this.$q = $q;

        this._taskUUID = null;
        this._dataObj = null;
    }

    /**
     * Updates the internal data object and emits an event indicating data update.
     * @param {Object} data - The new data to be set.
     */
    updateData(data) {
        this._dataObj = data;
        this.$signals.emit('versioningDataUpdated');
    }

    /**
     * Wrapper method to update data.
     * @param {Object} data - The new data to be set.
     */
    update(data) {
        this.updateData(data);
    }

    /**
     * Loads data for a given task UUID.
     * @param {string} taskUUID - The UUID of the task.
     * @param {boolean} [skipIfAlreadyLoaded=false] - Flag to skip loading if data is already loaded.
     * @returns {Promise} A promise that resolves when data is loaded.
     */
    loadData(taskUUID, skipIfAlreadyLoaded) {
        const future = this.$q.defer();

        if (typeof(skipIfAlreadyLoaded) !== 'undefined' && skipIfAlreadyLoaded === true && this._taskUUID === taskUUID && this._dataObj !== null) {
            console.warn("Skipping VersioningAndLockingService.loadData, already loaded");
            future.resolve();
        } else {
            this._taskUUID = taskUUID;
            this.APIService.versioningAndLockingAssessment(this._taskUUID).then(response => {
                this.updateData(response.data);
                future.resolve();
            });
        }

        return future.promise;
    }

    /**
     * Retrieves the current data object.
     * @returns {Object} The current data object.
     */
    getData() {
        return this._dataObj;
    }

    /**
     * Increments the patch version of the current data.
     * @returns {Promise} A promise that resolves when the patch version is incremented.
     */
    incrementPatchVersion() {
        const future = this.$q.defer();
        const r = this.APIService.versioningAndLockingAssessment(this._taskUUID, {version: 'patch_version'});
        if (r == null) {
            future.resolve();
        } else {
            r.then(response => {
                this.updateData(response.data);
                future.resolve();
            });
        }
        return future.promise;
    }

    /**
     * Opens a modal to tag the current version as a new version.
     */
    tagAsNewVersionModal() {
        if (!this._dataObj.locked) {
            this.$uibModal.open({
                templateUrl: "/views/ib/cms/versioningAndLocking/tagAsNewVersionModal.html",
                windowClass: "tag-as-new-version-modal",
                transclude : false,
                backdrop   : 'static',
                keyboard   : false,
                controller : function ($scope, $uibModalInstance, versionObj, taskUUID, APIService) {
                    $scope.major_version = versionObj.major_version;
                    $scope.minor_version = versionObj.minor_version;
                    $scope.patch_version = versionObj.patch_version;
                    $scope.version = versionObj.version;

                    const validateShortName = function (val) {
                        if (/[^a-zA-Z0-9]/.test(val)) {
                            return false;
                        }
                        return true;
                    };

                    $scope.onTabButton = function () {
                        $scope.shortNameHasError = !validateShortName($scope.shortName);

                        if ($scope.shortNameHasError) return false;

                        $scope.promise = APIService.versioningAndLockingAssessment(taskUUID, {
                            version  : 'minor_version',
                            shortName: $scope.shortName,
                            comment  : $scope.comment
                        });
                        $scope.promise.then(function (response) {
                            this.updateData(response.data);
                            $uibModalInstance.close();
                        }.bind(this));
                    }.bind(this);
                }.bind(this),
                resolve    : {
                    versionObj: () => this._dataObj,
                    taskUUID  : () => this._taskUUID
                }
            });
        }
    }

    /**
     * Opens a modal to lock the current version.
     */
    lockVersionModal() {
        if (!this._dataObj.locked) {
            this.$uibModal.open({
                templateUrl: "/views/ib/cms/versioningAndLocking/lockVersionModal.html",
                windowClass: "lock-version-modal",
                transclude : false,
                backdrop   : 'static',
                keyboard   : false,
                controller : function ($scope, $uibModalInstance, versionObj, taskUUID, APIService) {
                    $scope.major_version = versionObj.major_version;
                    $scope.minor_version = versionObj.minor_version;
                    $scope.patch_version = versionObj.patch_version;
                    $scope.version = versionObj.version;

                    const validateShortName = function (val) {
                        if (/[^a-zA-Z0-9]/.test(val)) {
                            return false;
                        }
                        return true;
                    };

                    $scope.onLockButton = function () {
                        $scope.shortNameHasError = !validateShortName($scope.shortName);

                        if ($scope.shortNameHasError) return false;

                        $scope.promise = APIService.versioningAndLockingAssessment(taskUUID, {
                            version  : 'major_version',
                            shortName: $scope.shortName,
                            comment  : $scope.comment
                        });
                        $scope.promise.then(function (response) {
                            this.updateData(response.data);
                            $uibModalInstance.close();
                        }.bind(this));
                    }.bind(this);
                }.bind(this),
                resolve    : {
                    versionObj: () => this._dataObj,
                    taskUUID  : () => this._taskUUID
                }
            });
        }
    }

    /**
     * Opens a modal to unlock the current version.
     */
    unlockVersionModal() {
        if (this._dataObj.locked) {
            this.$uibModal.open({
                templateUrl: "/views/ib/cms/versioningAndLocking/unlockVersionModal.html",
                windowClass: "unlock-version-modal",
                transclude : false,
                backdrop   : 'static',
                keyboard   : false,
                controller : function ($scope, $uibModalInstance, versionObj, taskUUID, APIService, $config) {
                    $scope.onUnlockButton = function () {
                        $scope.promise = APIService.versioningAndLockingAssessment(taskUUID, {unlock: 'True'});
                        $scope.promise.then(function (response) {
                            this.updateData(response.data);
                            $uibModalInstance.close();
                        }.bind(this));
                    }.bind(this);
                }.bind(this),
                resolve    : {
                    versionObj: () => this._dataObj,
                    taskUUID  : () => this._taskUUID
                }
            });
        }
    }

    /**
     * Opens a modal to mark the current version as available for work.
     */
    availableWDModal() {
        this.$uibModal.open({
            templateUrl: "/views/ib/cms/versioningAndLocking/availableWDModal.html",
            windowClass: "availableWD-modal",
            transclude : false,
            backdrop   : 'static',
            keyboard   : false,
            controller : function ($scope, $uibModalInstance, taskUUID, APIService, $config) {
                $scope.onAvailableWDButton = function () {
                    $scope.promise = APIService.versioningAndLockingAssessment(taskUUID, {availableWD: 1});
                    $scope.promise.then(function (response) {
                        this.updateData(response.data);
                        $uibModalInstance.close();
                    }.bind(this));
                }.bind(this);
            }.bind(this),
            resolve    : {
                versionObj: () => this._dataObj,
                taskUUID  : () => this._taskUUID
            }
        });
    }

    /**
     * Opens a modal to mark the current version as unavailable for work.
     */
    unavailableWDModal() {
        this.$uibModal.open({
            templateUrl: "/views/ib/cms/versioningAndLocking/unavailableWDModal.html",
            windowClass: "unavailableWD-modal",
            transclude : false,
            backdrop   : 'static',
            keyboard   : false,
            controller : function ($scope, $uibModalInstance, taskUUID, APIService, $config) {
                $scope.onUnavailableWDButton = function () {
                    $scope.promise = APIService.versioningAndLockingAssessment(taskUUID, {availableWD: 0});
                    $scope.promise.then(function (response) {
                        this.updateData(response.data);
                        $uibModalInstance.close();
                    }.bind(this));
                }.bind(this);
            }.bind(this),
            resolve    : {
                versionObj: () => this._dataObj,
                taskUUID  : () => this._taskUUID
            }
        });
    }

    /**
     * Opens a modal to view the audit log.
     */
    openAuditLogModal() {
        this.$uibModal.open({
            templateUrl: "/views/ib/cms/versioningAndLocking/auditLogModal.html",
            windowClass: "audit-log-modal",
            transclude : false,
            backdrop   : 'static',
            keyboard   : false,
            controller : function ($scope, $uibModalInstance, taskUUID, APIService, $config, $timeout, $route, $window, isLocked, $q) {
                $scope.isLocked = isLocked;

                $scope.isArray = function (val) {
                    return angular.isArray(val);
                };

                $scope.getVersionArray = function (val) {
                    const tmp = val.split(".");
                    const result = {
                        major_version: parseInt(tmp[0]),
                        minor_version: parseInt(tmp[1]),
                        patch_version: parseInt(tmp[2].split("-")[0])
                    };
                    return result;
                };

                $scope.getTagNr = function (index, meta) {
                    return index + 1 + ((meta.page - 1) * meta.limit);
                };

                let future = $q.defer();

                $scope.getNumber = function (num) {
                    return new Array(num);
                };
                $scope.getPage = function (page) {
                    future = $q.defer();

                    $timeout(function () {
                        $scope.$apply(function () {
                            $scope.auditlogPromise = future.promise;
                        });
                    });

                    $scope.data = null;

                    APIService.getLogs(taskUUID, {page: page, limit: 10}).then(function (response) {
                        $scope.data = response.data;
                        $scope.meta = response.meta;
                        future.resolve();
                    });

                };

                $scope.getPage(1);

                /*
                 * Rollback
                 */
                if (!isLocked) {
                    $scope.onRollback = function (tag) {
                        const onRollbackModal = this.$uibModal.open({
                            templateUrl: "/views/ib/cms/versioningAndLocking/rollbackModal.html",
                            windowClass: "rollback-modal",
                            transclude : false,
                            backdrop   : 'static',
                            keyboard   : false,
                            controller : function ($scope, $uibModalInstance, taskUUID, tag, APIService, $q) {
                                $scope.validConfirmStr = false;

                                const _generateData = function () {
                                    const tmp = tag.split(".");
                                    const result = {
                                        major_version: parseInt(tmp[0]),
                                        minor_version: parseInt(tmp[1]),
                                        patch_version: parseInt(tmp[2].split("-")[0]),
                                        checkout_tag : tag,
                                        comment      : [
                                            "Rollback to Version " + tag
                                        ]
                                    };
                                    if (angular.isDefined(tmp[2].split("-")[1])) {
                                        result['tag_short_name'] = tmp[2].split("-")[1];
                                    }
                                    if (angular.isDefined($scope.comment)) {
                                        result['comment'].push($scope.comment);
                                    }
                                    return result;
                                };

                                $scope.onRollback = function () {
                                    if ($scope.confirmStr === 'OK') {
                                        const future = $q.defer();
                                        $scope.rollbackPromise = future.promise;
                                        APIService.versioningAndLockingAssessment(taskUUID, _generateData()).then(function (response) {
                                            future.resolve();
                                            $uibModalInstance.dismiss('cancel');
                                        });
                                    }
                                };
                                $scope.onChange = function () {
                                    $scope.validConfirmStr = false;
                                    if ($scope.confirmStr === 'OK') {
                                        $scope.validConfirmStr = true;
                                    }
                                };
                            },
                            resolve    : {
                                taskUUID: function () {
                                    return taskUUID;
                                },
                                tag     : function () {
                                    return tag;
                                }
                            }
                        });

                        onRollbackModal.result.then(function () {

                        }, function () {
                            $scope.$close();
                            $route.reload();
                            $window.location.reload();
                        });
                    }.bind(this);
                }

            }.bind(this),
            resolve    : {
                taskUUID: () => this._taskUUID,
                isLocked: () => this._dataObj.locked
            }
        });
    }
}

export default VersioningAndLockingService;